import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { SessionExpiredService } from '../shared/session/session-expired.service';
import { isSuperAdminRequest } from './super-admin.interceptor';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const sessionExpired = inject(SessionExpiredService);

  // Superbeheer handles its own refusals (superAdminInterceptor): they must not end a beheer or member session.
  if (isSuperAdminRequest(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // A 401 on a request that carried a token means the session has ended: expired, blocked or no access.
      // Without a token (a visitor, or the login itself) the screen handles the error.
      if (error.status !== 401 || !req.headers.has('Authorization')) {
        return throwError(() => error);
      }

      // A screen with unsaved work (the update editor) stays: the login comes over it, and the request goes again.
      if (sessionExpired.isGuarding()) {
        return retryAfterSigningInAgain(req, next, error, sessionExpired, auth);
      }

      auth.logout();
      router.navigateByUrl('/inloggen');

      return throwError(() => error);
    })
  );
};

/** Waits for the login over the screen; signed in, the request goes again with the new token, else it stays refused. */
function retryAfterSigningInAgain(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: HttpErrorResponse,
  sessionExpired: SessionExpiredService,
  auth: AuthService,
): Observable<HttpEvent<unknown>> {
  return sessionExpired.askToSignInAgain().pipe(
    switchMap(signedIn =>
      signedIn
        ? next(req.clone({ setHeaders: { Authorization: `Bearer ${auth.getToken()}` } }))
        : throwError(() => error),
    ),
  );
}
